/**
 * Stacked overlapping avatars (React Native). No Tailwind / framer-motion.
 */

import React, { useMemo } from 'react';
import { View, Text } from 'react-native';
import { Image } from 'expo-image';
import type { ImageSourcePropType } from 'react-native';
import type { ImageStyle } from 'react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';
import { LayeredAvatarView } from '../../avatars/LayeredAvatarLab/LayeredAvatarView';
import { canRenderLayeredAvatar } from '../../avatars/LayeredAvatarLab/characterCustomizer.helper';
import type { EquippedMap } from '../../avatars/LayeredAvatarLab/characterCustomizer.types';
import { AVATAR_GROUP_DEFAULTS } from './AvatarGroup.constants';
import { getAvatarGroupStyles } from './AvatarGroup.styles';

export interface AvatarGroupItem {
  source: ImageSourcePropType | null;
  /**
   * Custom layered look. Wins over `source` (the ready-avatar image) exactly as it does on a
   * leaderboard row — a stack that only ever drew `source` showed a customised player their
   * old ready avatar forever, not merely until a refresh. Optional, so a caller with no look
   * to pass keeps today's behaviour by omission.
   */
  avatarConfig?: EquippedMap | null;
  label: string;
}

export interface AvatarGroupProps {
  avatars: AvatarGroupItem[];
  /** Total members in the group (for +N after visible faces). */
  totalMemberCount: number;
  maxVisible?: number;
  size?: number;
  overlap?: number;
  /** Border color matching the parent card surface (stack ring). */
  ringColor: string;
  /** Prefix for React keys (e.g. group id). */
  keyPrefix: string;
}

function initialFromLabel(label: string): string {
  const t = label.trim();
  if (t.length === 0) return '?';
  return t.charAt(0).toUpperCase();
}

export const AvatarGroup: React.FC<AvatarGroupProps> = ({
  avatars,
  totalMemberCount,
  maxVisible = AVATAR_GROUP_DEFAULTS.MAX_VISIBLE,
  size = AVATAR_GROUP_DEFAULTS.SIZE,
  overlap = AVATAR_GROUP_DEFAULTS.OVERLAP,
  ringColor,
  keyPrefix,
}) => {
  const theme = useTheme();
  const styles = useMemo(
    () =>
      getAvatarGroupStyles(theme, {
        size,
        overlap,
        ringColor,
      }),
    [theme, size, overlap, ringColor],
  );

  const stackZStyles = useMemo(
    () =>
      [
        styles.stackZ6,
        styles.stackZ5,
        styles.stackZ4,
        styles.stackZ3,
        styles.stackZ2,
        styles.stackZ1,
      ] as const,
    [styles],
  );

  const visible = avatars.slice(0, maxVisible);
  const extraCount = Math.max(0, totalMemberCount - visible.length);

  return (
    <View style={styles.row}>
      <View style={styles.stackRow}>
        {visible.map((item, idx) => {
          const isFirst = idx === 0;
          const stackZ = stackZStyles[Math.min(idx, stackZStyles.length - 1)] ?? styles.stackZ0;
          return (
            <View
              key={`${keyPrefix}-ag-${idx}`}
              style={[styles.avatarSlot, stackZ, isFirst && styles.avatarSlotFirst]}
              accessibilityLabel={item.label || `Member ${idx + 1}`}
              accessibilityRole="image"
            >
              <View style={styles.avatarRing}>
                {/*
                  The guard is `canRenderLayeredAvatar`, not just "is there a config": an app
                  that predates a newly-added base must fall through to the ready avatar rather
                  than draw a hole. That leaves the image and initials paths exactly as they were.
                */}
                {item.avatarConfig && canRenderLayeredAvatar(item.avatarConfig) ? (
                  <LayeredAvatarView equipped={item.avatarConfig} size={size} />
                ) : item.source ? (
                  <Image
                    source={item.source}
                    style={styles.avatarImage as ImageStyle}
                    contentFit="cover"
                    alt={WEB_IMAGE_ALT_DECORATIVE}
                  />
                ) : (
                  <View style={styles.avatarFallback}>
                    <Text style={styles.avatarFallbackText}>{initialFromLabel(item.label)}</Text>
                  </View>
                )}
              </View>
            </View>
          );
        })}
        {extraCount > 0 ? (
          <View
            style={[styles.plusChip, styles.plusZ]}
            accessibilityLabel={`${extraCount} more members`}
          >
            <Text style={styles.plusChipText}>+{extraCount}</Text>
          </View>
        ) : null}
      </View>
    </View>
  );
};
